'use client'

import { type FormatXMLElementFn } from 'intl-messageformat'
import { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { I18nProvider as RacI18nProvider } from 'react-aria-components/I18nProvider'
import {
  type IntlConfig,
  IntlProvider,
  MissingTranslationError,
} from 'react-intl'

import { apmCaptureError } from '../../../apm'
import { type Messages } from '../../i18nTypes'
import { type LOCALE } from '../../LOCALE'
import { I18nBaseUiLocaleContext } from '../i18nBaseUiLocaleContext'

import { customElements } from './customElements'
import { htmlElements } from './htmlElements'

const defaultRichTextElements: Record<string, FormatXMLElementFn<ReactNode>> = {
  ...htmlElements,
  ...customElements,
}

/* eslint-disable no-console -- logging during development */
const onError: IntlConfig['onError'] = (error) => {
  if (process.env.NODE_ENV === 'production') {
    apmCaptureError(error)
  } else if (error instanceof MissingTranslationError) {
    /**
     * Skip logging if `defaultMessage` is used for temporary translation in development
     */
    if (error.descriptor?.defaultMessage) {
      return
    }

    console.warn(
      `${error.message}\nRun "yarn install && yarn workspace app run compile-translations" to update locale files.\n`,
    )
  } else {
    console.warn(error)
  }
}
/* eslint-enable no-console */

type LocaleProviderProps = PropsWithChildren<{
  locale: LOCALE
  localeMessages: Messages
  timeZone: string
}>

export const I18nProvider: FC<LocaleProviderProps> = ({
  children,
  locale,
  localeMessages,
  timeZone,
}) => (
  <IntlProvider
    defaultRichTextElements={defaultRichTextElements}
    locale={locale}
    messages={localeMessages}
    onError={onError}
    timeZone={timeZone}
  >
    <I18nBaseUiLocaleContext>
      <RacI18nProvider locale={locale}>{children}</RacI18nProvider>
    </I18nBaseUiLocaleContext>
  </IntlProvider>
)
